import React, { useContext, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { Alert, Badge, Tooltip } from "@mui/material";

import { MuiTabPanel } from "../../../components/mui/MuiTabPanel";
import { SearchBar } from "../../../components/SearchBar";
import { StyledTabItem } from "../../../components/StyledTabItem";
import { StyledTabs } from "../../../components/StyledTabs";
import { UserRole } from "../../../consts/user-role";
import { useAuth } from "../../../hooks/useAuth";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch";
import { useTabs } from "../../../hooks/useTabs";
import { groupVariablesBySection } from "../../../utils/group-variables-by-secion";
import { mapStringToVariableInfo } from "../../../utils/map-string-to-variable-Info";
import { useSearchVariables } from "../api/useSearchVariables";
import { CafsContext } from "../CafsContext";
import { GuestsList } from "../components/cafs-sections/cafs-guests/GuestsList";
import { ProductsList } from "../components/cafs-sections/cafs-products/ProductsList";
import { PlantVariables } from "../components/plant-variables/PlantVariables";
import { ReferencesExport } from "../components/ReferencesExport";
import { CafsSection } from "../const/cafs-section";

export function PlantTabs({ ...props }) {
  const [urlSearchParams] = useSearchParams();
  const { cafs } = useContext(CafsContext);
  const { tabIndex, handleTabChange } = useTabs();
  const { formatMessage } = useIntl();
  const { isFarmTreeRole, user } = useAuth();
  const canExportReferences =
    isFarmTreeRole || user?.Role === UserRole.ProjectOwner;
  const { refetch, variablesInfo } = useSearchVariables();

  const [searchParamValue, setSearchParamValue] = useDebouncedSearch(
    "search",
    300,
  );

  useEffect(() => {
    refetch();
  }, [urlSearchParams]);

  const mappedVariablesInfo = mapStringToVariableInfo(variablesInfo ?? []);
  const sectionsMap = groupVariablesBySection(mappedVariablesInfo);

  const plantSections = [
    CafsSection.PlantGrowth,
    CafsSection.PlantLifeCycle,
    CafsSection.PlantWoodDensity,
    CafsSection.PlantBiomassAllocation,
    CafsSection.PlantRoot,
    CafsSection.PlantConstraint,
    CafsSection.PlantGrowthCondition,
    CafsSection.PlantCost,
    CafsSection.PlantLabour,
    CafsSection.PlantBiodiversity,
    CafsSection.PlantResponseToPressure,
  ];

  const plantItems = plantSections
    .map(section => sectionsMap.get(section) || [])
    .reduce((acc, sectionItems) => acc.concat(sectionItems), [])
    .filter((item, index, self) => self.indexOf(item) === index);

  return (
    !!cafs?.EnglishName?.Current?.length && (
      <div className="p-4 border-[1px] border-solid border-gray-200 rounded mt-4 bg-gray-50">
        <div className="mb-6">
          <SearchBar
            initialValue={searchParamValue ?? ""}
            onChange={value => setSearchParamValue(value)}
            searchIcon
            placeholder="Search for CAFS variables"
          />
        </div>
        <div className="border-[1px] border-gray w-full">
          <StyledTabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="plant tabs">
            <StyledTabItem
              selected={tabIndex === 0}
              label={
                <Tooltip placement="top-start" title={plantItems?.join(", ")}>
                  <Badge
                    badgeContent={plantItems.length}
                    color="secondary"
                    sx={{
                      ".MuiBadge-badge": {
                        right: "-14px",
                      },
                    }}>
                    {formatMessage({
                      id: "CAFS.PLANT_TAB_HEADER",
                      defaultMessage: "Plant",
                      description: "Plant tab header on cafs view",
                    })}
                  </Badge>
                </Tooltip>
              }
              index={0}
            />

            <StyledTabItem
              selected={tabIndex === 1}
              label={
                <Tooltip
                  placement="top-start"
                  title={sectionsMap.get("PlantProduct")?.join(", ")}>
                  <Badge
                    badgeContent={sectionsMap.get("PlantProduct")?.length}
                    color="secondary"
                    sx={{
                      ".MuiBadge-badge": {
                        right: "-14px",
                      },
                    }}>
                    {formatMessage({
                      id: "CAFS.PRODUCTS_TAB_HEADER",
                      defaultMessage: "Products",
                      description: "Products tab header on cafs view",
                    })}
                  </Badge>
                </Tooltip>
              }
              index={1}
            />

            <StyledTabItem
              selected={tabIndex === 2}
              label={
                <Tooltip
                  placement="top-start"
                  title={sectionsMap.get("Guest")?.join(", ")}>
                  <Badge
                    badgeContent={sectionsMap.get("Guest")?.length}
                    sx={{
                      ".MuiBadge-badge": {
                        right: "-14px",
                      },
                    }}
                    color="secondary">
                    {formatMessage({
                      id: "CAFS.GUESTS_TAB_HEADER",
                      defaultMessage: "Guests",
                      description: "Guests tab header on cafs view",
                    })}
                  </Badge>
                </Tooltip>
              }
              index={2}
            />
          </StyledTabs>
        </div>

        <MuiTabPanel index={0} value={tabIndex}>
          <ErrorBoundary
            fallback={
              <Alert severity="error">
                <FormattedMessage
                  id="ERROR.FETCHING_CAFS_DATA"
                  defaultMessage="Something went wrong when fetching cafs data..."
                  description="Error message when fetching cafs data problem"
                />
              </Alert>
            }>
            <PlantVariables variables={sectionsMap} />
          </ErrorBoundary>
        </MuiTabPanel>

        <MuiTabPanel index={1} value={tabIndex}>
          <ErrorBoundary
            fallback={
              <Alert severity="error">
                <FormattedMessage
                  id="ERROR.FETCHING_PRODUCTS_DATA"
                  defaultMessage="Something went wrong when fetching products data..."
                  description="Error message when fetching products data problem"
                />
              </Alert>
            }>
            <ProductsList variables={sectionsMap.get("PlantProduct")} />
          </ErrorBoundary>
        </MuiTabPanel>

        <MuiTabPanel index={2} value={tabIndex}>
          <ErrorBoundary
            fallback={
              <Alert severity="error">
                <FormattedMessage
                  id="ERROR.FETCHING_GUESTS_DATA"
                  defaultMessage="Something went wrong when fetching guests data..."
                  description="Error message when fetching guests data problem"
                />
              </Alert>
            }>
            <GuestsList variables={sectionsMap.get("Guest")} />
          </ErrorBoundary>
        </MuiTabPanel>
        {canExportReferences && <ReferencesExport />}
      </div>
    )
  );
}
